/*
 * shipping-countdown.js
 * Drives [data-shipping-countdown] strips: a live HH:MM:SS countdown to the
 * order cutoff of the next OPEN shipping day, evaluated in the section's IANA
 * timezone. If today is open and the cutoff hasn't passed, it counts down to
 * today's cutoff ("ships today"). Otherwise — today closed, or cutoff already
 * passed — it rolls forward to the next open day's cutoff and swaps the message
 * to name that day. No dependencies, no "expired" state.
 *
 * Contract (rendered by sections/shipping-countdown.liquid):
 *   [data-shipping-countdown]      root element
 *       data-cutoff="HH:MM"        daily cutoff, wall-clock in data-timezone
 *       data-timezone="IANA"       e.g. "America/New_York"
 *       data-open-days="1,2,..."   open weekday indices, 0=Sun … 6=Sat
 *                                  (empty → treated as open every day)
 *       data-line-today="..."      top-line copy when shipping today
 *       data-line-tomorrow="..."   top-line copy when the next open day is
 *                                  literally tomorrow (falls back to line-next)
 *       data-line-next="...{day}"  top-line copy otherwise ({day} → day name)
 *   [data-hours] [data-minutes] [data-seconds]   the number nodes
 *   .builder-shipping-countdown__line1            the top-line node (optional)
 *
 * Graceful degradation: any parse/Intl error leaves the "--" placeholders in
 * place and the strip untouched, per strip (one failure never blocks others).
 */
(function () {
  'use strict';

  var DAY = 86400;
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  // Current wall-clock in `tz` → { sec: seconds since local midnight (0–86399),
  // dow: weekday index 0=Sun…6=Sat }, or null on any parse glitch.
  function nowInTz(tz) {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).formatToParts(new Date());

    var map = {};
    for (var i = 0; i < parts.length; i++) {
      map[parts[i].type] = parts[i].value;
    }

    var hour = parseInt(map.hour, 10) % 24; // some engines emit "24" at midnight
    var min = parseInt(map.minute, 10);
    var sec = parseInt(map.second, 10);
    var dow = DOW[map.weekday];

    if (isNaN(hour) || isNaN(min) || isNaN(sec) || dow === undefined) return null;
    return { sec: hour * 3600 + min * 60 + sec, dow: dow };
  }

  // Parse "1,2,6" → { 1:true, 2:true, 6:true }. Empty/none → every day open.
  function parseOpenDays(attr) {
    var open = {};
    var any = false;
    var raw = (attr || '').split(',');
    for (var i = 0; i < raw.length; i++) {
      var n = parseInt(raw[i], 10);
      if (n >= 0 && n <= 6) {
        open[n] = true;
        any = true;
      }
    }
    if (!any) {
      for (var d = 0; d < 7; d++) open[d] = true;
    }
    return open;
  }

  function init(root) {
    var cutoff = (root.getAttribute('data-cutoff') || '').trim();
    var tz = (root.getAttribute('data-timezone') || 'America/New_York').trim();
    var openDays = parseOpenDays(root.getAttribute('data-open-days'));
    var lineToday = root.getAttribute('data-line-today') || '';
    var lineTomorrow = root.getAttribute('data-line-tomorrow') || '';
    var lineNext = root.getAttribute('data-line-next') || '';

    var hm = cutoff.match(/^(\d{1,2}):(\d{2})$/);
    if (!hm) return; // invalid cutoff → keep placeholders
    var cutoffSec = parseInt(hm[1], 10) * 3600 + parseInt(hm[2], 10) * 60;
    if (cutoffSec < 0 || cutoffSec > DAY) return;

    // Bail out (keep placeholders) if the timezone is not supported here.
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz });
    } catch (e) {
      return;
    }

    var hEl = root.querySelector('[data-hours]');
    var mEl = root.querySelector('[data-minutes]');
    var sEl = root.querySelector('[data-seconds]');
    if (!hEl || !mEl || !sEl) return;

    var line1El = root.querySelector('.builder-shipping-countdown__line1');

    function tick() {
      var now = nowInTz(tz);
      if (now === null) return; // transient glitch → retry next tick

      // Walk forward from today to the first open day whose cutoff is still
      // ahead. d=0 is today (only if open AND before cutoff); otherwise skip to
      // the next open day. Guaranteed to resolve within 7 days when any day is
      // open (parseOpenDays opens all days when none are configured).
      var remaining = null;
      var targetDow = now.dow;
      var daysAhead = 0;
      for (var d = 0; d < 8; d++) {
        var dow = (now.dow + d) % 7;
        if (!openDays[dow]) continue;
        if (d === 0 && now.sec >= cutoffSec) continue; // today's cutoff passed
        remaining = d * DAY + (cutoffSec - now.sec);
        targetDow = dow;
        daysAhead = d;
        break;
      }
      if (remaining === null) return; // no open day found → leave placeholders

      hEl.textContent = pad(Math.floor(remaining / 3600));
      mEl.textContent = pad(Math.floor((remaining % 3600) / 60));
      sEl.textContent = pad(remaining % 60);

      if (line1El) {
        if (daysAhead === 0) {
          line1El.textContent = lineToday;
        } else if (daysAhead === 1 && lineTomorrow) {
          line1El.textContent = lineTomorrow;
        } else {
          line1El.textContent = lineNext.replace('{day}', DAY_NAMES[targetDow]);
        }
      }
    }

    tick();
    setInterval(tick, 1000);
  }

  var roots = document.querySelectorAll('[data-shipping-countdown]');
  for (var i = 0; i < roots.length; i++) {
    try {
      init(roots[i]);
    } catch (e) {
      // Keep this strip's placeholders; never break the page.
    }
  }
})();